import { z } from 'zod';
import { pipe, Option, Array as A, Order } from 'effect';

import { storage } from '@/lib/storage';
import { isServiceColumn } from '@/components/AgGrid/helpers';

// the payments grid data columns in their order (array order), with visibility, pinning (LTR) and the width
// the user gave them
export const GridColumnLayoutSchema = z.array(
  z.object({
    colId: z.string(),
    hide: z.boolean(),
    pinned: z.enum(['left', 'right']).nullable().optional(),
    width: z.number().positive().optional(),
  }),
);
export type GridColumnLayout = z.infer<typeof GridColumnLayoutSchema>;

const StoredLayoutEntrySchema = z.object({ columns: GridColumnLayoutSchema });

// entries of other users are kept as they are when one entry is written
const StoredConfigSchema = z.record(z.string(), z.unknown());
type StoredConfig = z.infer<typeof StoredConfigSchema>;

type Pinned = 'left' | 'right' | null;
type ColumnStateLike = {
  colId?: string | null;
  field?: string | null;
  hide?: boolean | null;
  pinned?: Pinned | boolean;
  width?: number | null;
};
type ColumnDefLike = { field: string; hide?: boolean | null; pinned?: Pinned | boolean; width?: number };

const GRID_LAYOUT_SCOPE = 'grid';

const safeJSONParse = (raw: string | null): Option.Option<unknown> =>
  pipe(Option.fromNullable(raw), Option.flatMap(Option.liftThrowable((value: string): unknown => JSON.parse(value))));

// one saved layout for the whole grid, shared by every tab and category
export const getColumnLayoutScope = (): string => GRID_LAYOUT_SCOPE;

// the in-session column state belongs to one tab or category: another one can have other columns
export const getColumnsSessionScope = (
  flowState: string | null | undefined,
  category: string | null | undefined,
): string | null =>
  pipe(
    Option.fromNullable(flowState || null),
    Option.map((state) => `flowState:${state}`),
    Option.orElse(() =>
      pipe(
        Option.fromNullable(category || null),
        Option.map((id) => `category:${id}`),
      ),
    ),
    Option.getOrNull,
  );

export const getColumnLayoutStorageKey = (userId: string | null | undefined, scope: string | null) =>
  userId && scope ? `${userId}/${scope}` : null;

const parseStoredConfig = (raw: string | null): StoredConfig =>
  pipe(
    safeJSONParse(raw),
    Option.flatMap((value) => Option.fromNullable(StoredConfigSchema.safeParse(value).data)),
    Option.getOrElse((): StoredConfig => ({})),
  );

export const readColumnLayout = (raw: string | null, key: string): GridColumnLayout =>
  pipe(
    Option.fromNullable(parseStoredConfig(raw)[key]),
    Option.flatMap((entry) => Option.fromNullable(StoredLayoutEntrySchema.safeParse(entry).data)),
    Option.map(({ columns }) => columns),
    Option.getOrElse((): GridColumnLayout => []),
  );

export const writeColumnLayout = (raw: string | null, key: string, layout: GridColumnLayout): string =>
  JSON.stringify({ ...parseStoredConfig(raw), [key]: { columns: layout } });

const toPinned = (pinned: Pinned | boolean | undefined): Pinned => {
  if (pinned === 'left' || pinned === true) return 'left';
  return pinned === 'right' ? 'right' : null;
};

export const toColumnLayout = (columns: ReadonlyArray<ColumnStateLike>): GridColumnLayout =>
  pipe(
    columns,
    A.filterMap(({ colId, field, hide, pinned, width }) =>
      pipe(
        Option.fromNullable(colId ?? field),
        Option.filter((id) => !isServiceColumn(id)),
        Option.map((id) => ({
          colId: id,
          hide: Boolean(hide),
          pinned: toPinned(pinned),
          ...(width && width > 0 ? { width: Math.round(width) } : {}),
        })),
      ),
    ),
  );

// what the column menu changes: order and visibility, without widths the grid refits by itself
export const toColumnOrderAndVisibility = (columns: ReadonlyArray<ColumnStateLike>) =>
  toColumnLayout(columns).map(({ colId, hide }) => ({ colId, hide }));

// data columns found in the layout are rearranged inside the positions they hold in the default order and take
// the saved visibility, pinning and width; service columns and columns unknown to the layout keep their defaults
export const applyColumnLayout = <T extends ColumnDefLike>(
  columns: ReadonlyArray<T>,
  layout: GridColumnLayout,
): T[] => {
  const savedIndexById = new Map(layout.map(({ colId }, index) => [colId, index] as const));
  const savedIndex = (column: T) => Option.fromNullable(savedIndexById.get(column.field));
  const isSaved = (column: T) => !isServiceColumn(column.field) && Option.isSome(savedIndex(column));

  const savedInLayoutOrder = pipe(
    columns,
    A.filter(isSaved),
    A.sort(Order.mapInput(Order.number, (column: T) => Option.getOrElse(savedIndex(column), () => 0))),
    A.map((column) =>
      pipe(
        savedIndex(column),
        Option.map((index) => {
          const { hide, pinned, width } = layout[index];
          return {
            ...column,
            hide,
            ...(pinned !== undefined ? { pinned } : {}),
            ...(width !== undefined ? { width } : {}),
          };
        }),
        Option.getOrElse(() => column),
      ),
    ),
  );

  const [, result] = A.mapAccum(columns, 0, (slot, column): [number, T] =>
    isSaved(column) ? [slot + 1, savedInLayoutOrder[slot]] : [slot, column],
  );

  return result;
};

// saved widths as the minimums of sizeColumnsToFit: fitting may widen a column to fill free space but never
// shrinks it below the width the user gave it
export const toFitColumnLimits = (layout: GridColumnLayout) =>
  pipe(
    layout,
    A.filterMap(({ colId, width }) =>
      pipe(
        Option.fromNullable(width),
        Option.map((minWidth) => ({ key: colId, minWidth })),
      ),
    ),
  );

// keeps the saved columns the current grid lacks, each right after the column it followed (or first)
export const mergeColumnLayout = (previous: GridColumnLayout, current: GridColumnLayout): GridColumnLayout => {
  const currentIds = new Set(current.map(({ colId }) => colId));

  return A.reduce(previous, current, (merged, column, index) => {
    if (currentIds.has(column.colId)) return merged;

    const anchorIndex = pipe(
      previous.slice(0, index),
      A.reverse,
      A.findFirst((preceding) => merged.some(({ colId }) => colId === preceding.colId)),
      Option.map((preceding) => merged.findIndex(({ colId }) => colId === preceding.colId)),
      Option.getOrElse(() => -1),
    );

    return [...merged.slice(0, anchorIndex + 1), column, ...merged.slice(anchorIndex + 1)];
  });
};

// a width is saved only for the columns the user resized, the others keep the width saved before (if any):
// a width the grid fitted to the screen would otherwise become the column's minimum on every other screen
export const keepUserSizedWidths = (
  previous: GridColumnLayout,
  current: GridColumnLayout,
  sizedColIds: ReadonlySet<string>,
): GridColumnLayout => {
  const previousWidthById = new Map(
    previous.flatMap(({ colId, width }) => (width === undefined ? [] : [[colId, width] as const])),
  );

  return current.map(({ width, ...column }) => {
    const kept = sizedColIds.has(column.colId) ? width : previousWidthById.get(column.colId);
    return kept === undefined ? column : { ...column, width: kept };
  });
};

export const getPaymentsGridColumnLayout = (key: string | null): GridColumnLayout =>
  key ? readColumnLayout(storage().paymentsAgGridConfig().get(), key) : [];

export const setPaymentsGridColumnLayout = (
  key: string | null,
  layout: GridColumnLayout,
  sizedColIds: ReadonlySet<string> = new Set(),
): void => {
  if (key) {
    const field = storage().paymentsAgGridConfig();
    const raw = field.get();
    const previous = readColumnLayout(raw, key);
    field.set(
      writeColumnLayout(raw, key, mergeColumnLayout(previous, keepUserSizedWidths(previous, layout, sizedColIds))),
    );
  }
};
